/**
 * The Tools panel's Transfer section (web): a warm transfer of this room's
 * call to a colleague (docs/interpreter-transfer-handover.md).
 *
 * The offer's life lives in the shared hook (../../useCallTransfer.ts), which
 * Malka Terp's call tools use too. A waiting offer is reported with
 * setInterpreterToolsTransferPending, which the Tools key shows while the
 * panel is minimized. The panel decides when the section applies (a queue
 * call in this room; sections.ts).
 */
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { withPixelLineHeight } from '../../../base/styles/functions.web';
import Button from '../../../base/ui/components/web/Button';
import { BUTTON_TYPES } from '../../../base/ui/constants.any';
import { shiftAccountKey } from '../../../vrs-layout/components/useShiftBreaks';
import { setInterpreterToolsTransferPending } from '../../actions.web';
import useCallTransfer, { candidateLabel } from '../../useCallTransfer';
const useStyles = makeStyles()(theme => ({
    controls: { display: 'flex', flexDirection: 'column', gap: theme.spacing(2) },
    button: { transition: 'none', border: `1px solid ${theme.palette.ui04}`, minHeight: 48, width: '100%', whiteSpace: 'normal', textAlign: 'start',
        '&:focus-visible': { outline: `2px solid ${theme.palette.focus01}`, outlineOffset: 2 } },
    note: { ...withPixelLineHeight(theme.typography.bodyShortRegular), marginTop: theme.spacing(2), overflowWrap: 'anywhere' }
}));
/**
 * Lists the colleagues who may take this call, by name, and offers it to the
 * chosen one. Colleagues load when the Tools panel opens; an offer stays
 * pending, open or minimized, until the server says how it ended, and may be
 * withdrawn meanwhile.
 *
 * @param {Object} props - The props.
 * @param {string} [props.callId] - This room's queue call.
 * @param {boolean} props.open - Whether the Tools panel is open.
 * @returns {ReactElement}
 */
export default function TransferSection({ callId, open }) {
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const transfer = useCallTransfer({ account: shiftAccountKey(), callId, currentAccount: shiftAccountKey, open });
    const pending = Boolean(transfer.pending);
    // The Tools key shows a waiting transfer while the panel is minimized.
    useEffect(() => {
        dispatch(setInterpreterToolsTransferPending(pending));
    }, [dispatch, pending]);
    useEffect(() => () => {
        dispatch(setInterpreterToolsTransferPending(false));
    }, [dispatch]);
    return React.createElement("div", null,
        React.createElement("div", { className: classes.controls },
            transfer.handedOver ? null
                : transfer.pending ? React.createElement(React.Fragment, null,
                    React.createElement("div", { role: 'status' }, transfer.pending.text),
                    transfer.pending.phase !== 'sending' && React.createElement(Button, { className: classes.button, disabled: transfer.pending.phase === 'withdrawing', label: 'Withdraw', onClick: transfer.withdraw, type: BUTTON_TYPES.TERTIARY }))
                    : transfer.status === 'idle' ? null
                        : transfer.status === 'loading' ? React.createElement("div", { role: 'status' }, "Loading available interpreters\u2026")
                            : transfer.status === 'failed' ? React.createElement("div", { role: 'status' }, "Couldn\u2019t load interpreters. Try again.")
                                : transfer.candidates.length ? transfer.candidates.map(candidate => React.createElement(Button, { className: classes.button, key: candidate.id, label: candidateLabel(candidate), onClick: () => transfer.offer(candidate), type: BUTTON_TYPES.TERTIARY }))
                                    : React.createElement("div", null, "No other interpreters available"),
            !transfer.pending && !transfer.handedOver && transfer.status !== 'idle' && React.createElement(Button, { className: classes.button, label: 'Refresh interpreters', onClick: transfer.reload, type: BUTTON_TYPES.TERTIARY })),
        React.createElement("div", { "aria-live": 'polite', className: classes.note }, transfer.note));
}
